/**
 * The block palette down the right of the canvas, shown while no block is
 * selected.
 *
 * It offers only what the plan language can express — a block that looked
 * draggable but that Radar could never run would be a lie — so the four
 * headings from the design each carry the step types that belong to them.
 * Start is listed but not draggable: it is the Scenario, not a step, and there
 * is only ever one.
 */

import {
	BranchesOutlined,
	CaretRightOutlined,
	FlagOutlined,
	MessageOutlined,
	UsergroupAddOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { AOPPanelResizeHandle } from './AOPPanelResizeHandle';
import React from 'react';
import { AOPStepType } from '../../types/AOPTypes';
import {
	AOPPaletteItem,
	AOP_GROUP_TEXT_CLASS,
	AOP_PALETTE_GROUP_LABELS,
	AOP_PALETTE_ITEMS,
} from './utils/AOPPlanUtils';

/** Read by the canvas's drop handler. */
export const AOP_PALETTE_DRAG_TYPE = 'application/x-aop-step';

const paletteIcon = (item: AOPPaletteItem): React.ReactNode => {
	switch (item.stepType) {
		case 'communicate':
			return <MessageOutlined />;
		case 'operate':
			return <UsergroupAddOutlined />;
		case 'decision':
			return <BranchesOutlined />;
		case 'finish':
			return <FlagOutlined />;
		default:
			return <CaretRightOutlined />;
	}
};

interface AOPPaletteProps {
	/** Click-to-add, for keyboard users and anyone who would rather not drag. */
	onAdd: (stepType: AOPStepType) => void;
	width: number;
	onResizePointerDown: (event: React.PointerEvent) => void;
	onResizeKeyDown: (event: React.KeyboardEvent) => void;
}

export const AOPPalette: React.FC<AOPPaletteProps> = ({
	onAdd,
	width,
	onResizePointerDown,
	onResizeKeyDown,
}) => (
	// `relative` and not scrolling: the resize handle sits proud of the left edge
	// and an overflow here would cut it in half. The scrolling column is inside.
	<aside
		className="relative shrink-0 border-0 border-l border-solid border-grey-4 bg-grey-1"
		style={{ width }}
		aria-label="Blocks"
	>
		<AOPPanelResizeHandle onPointerDown={onResizePointerDown} onKeyDown={onResizeKeyDown} />

		<div className="h-full overflow-y-auto">
			<p className="footnoteregular m-0 border-0 border-b border-solid border-grey-4 py-s pl-m pr-s uppercase tracking-wider text-grey-6">
				Drag onto the canvas to add
			</p>

			{AOP_PALETTE_GROUP_LABELS.map((group) => {
				const items = AOP_PALETTE_ITEMS.filter((item) => item.group === group.group);

				if (items.length === 0) {
					return null;
				}

				return (
					<div
						key={group.group}
						className="border-0 border-b border-solid border-grey-4 pb-xs pt-s"
					>
						<p className="footnoteregular m-0 px-s pb-2xs uppercase tracking-wider text-grey-6">
							{group.label}
						</p>

						{items.map((item) => (
							<Tooltip
								key={item.label}
								placement="left"
								title={
									item.unavailable
										? `${group.label} — ${item.label}. ${item.unavailable}.`
										: `${group.label} — ${item.label} (drag onto the canvas)`
								}
							>
								{/*
								  `aria-disabled`, not `disabled`: Tailwind's preflight is off, so
								  a natively disabled button keeps the browser's own chrome and
								  renders as a grey pill. Start is listed looking like the rest —
								  it is on the canvas already — and simply does nothing.
								*/}
								<button
									type="button"
									draggable={!item.unavailable}
									aria-disabled={item.unavailable ? true : undefined}
									onDragStart={(event) => {
										if (!item.stepType) {
											event.preventDefault();
											return;
										}

										event.dataTransfer.setData(AOP_PALETTE_DRAG_TYPE, item.stepType);
										event.dataTransfer.effectAllowed = 'copy';
									}}
									onClick={() => {
										if (item.stepType) {
											onAdd(item.stepType);
										}
									}}
									className={`bodyregular flex w-full items-center gap-xs border-0 bg-transparent px-s py-2xs text-left text-character-primary ${
										item.unavailable
											? 'cursor-default'
											: 'cursor-grab hover:bg-purple-1 active:cursor-grabbing'
									}`}
								>
									<span
										className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-grey-3 ${
											AOP_GROUP_TEXT_CLASS[item.group]
										}`}
									>
										{paletteIcon(item)}
									</span>
									{item.label}
								</button>
							</Tooltip>
						))}
					</div>
				);
			})}
		</div>
	</aside>
);
